// Multi-strategy store (LAB) — a "book" of strategies per security+timeframe,
// shared across the chart window and the Build side panel via chrome.storage
// (so writes in one window fire onStorageChange in the other). One strategy is
// "active" and drives the chart; the chart writes computed stats back here for
// the panel's sub-box summaries.
import { storageGet, storageSet } from "./storage.js";
import type { SavedStudio } from "./studio-model.js";

export interface StoredStrategy extends SavedStudio { id: string }
export interface StudioBook { strategies: StoredStrategy[]; activeId: string | null }
/** Compact stats the chart computes for the active strategy, for the panel to show. */
export interface StudioStats { id: string; trades: number; winPct: number; expectancyR: number; ready: boolean }

// Keyed by symbol only — a strategy belongs to the security, and the chart
// evaluates it on whatever timeframe it's showing.
const BOOK = (sym: string) => `labStudioBook:${sym}`;
const STATS = (sym: string) => `labStudioStats:${sym}`;
export const bookStorageKey = BOOK;
export const statsStorageKey = STATS;

export async function loadBook(sym: string): Promise<StudioBook> {
  const b = await storageGet<StudioBook | null>(BOOK(sym), null);
  if (b && Array.isArray(b.strategies)) return b;
  return { strategies: [], activeId: null };
}
export async function saveBook(sym: string, book: StudioBook): Promise<void> {
  await storageSet(BOOK(sym), book);
}
export async function loadStats(sym: string): Promise<StudioStats | null> {
  return storageGet<StudioStats | null>(STATS(sym), null);
}
export async function saveStats(sym: string, s: StudioStats): Promise<void> {
  await storageSet(STATS(sym), s);
}

export function newStrategyId(): string {
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}
export function blankStoredStrategy(name: string): StoredStrategy {
  return { id: newStrategyId(), name, whys: [], manage: { dir: "long", mode: "levels", swingLB: 10, rMult: 2 }, riskPct: 1 };
}
/** The strategy currently SELECTED for the chart, or null when nothing is
 * selected. Unlike a fallback lookup, a null activeId means "no selection" even
 * if strategies exist — the flask stays greyed until one is explicitly chosen. */
export function selectedStrategy(book: StudioBook): StoredStrategy | null {
  return book.activeId ? book.strategies.find((s) => s.id === book.activeId) ?? null : null;
}
/** @deprecated prefer selectedStrategy — kept for callers that want a fallback. */
export function activeStrategy(book: StudioBook): StoredStrategy | null {
  return book.strategies.find((s) => s.id === book.activeId) ?? book.strategies[0] ?? null;
}
/** A fresh auto-name ("Strategy 1", "Strategy 2", …) that doesn't collide. */
export function nextStrategyName(book: StudioBook): string {
  const taken = new Set(book.strategies.map((s) => s.name));
  for (let n = 1; ; n++) { const name = `Strategy ${n}`; if (!taken.has(name)) return name; }
}
/** Add a blank strategy and select it (so the flask lights up ready to author). */
export function addStrategy(book: StudioBook, name?: string): StoredStrategy {
  const s = blankStoredStrategy(name ?? nextStrategyName(book));
  book.strategies.push(s);
  book.activeId = s.id;
  return s;
}
/** Duplicate an existing strategy (deep copy of its settings) and select it. */
export function duplicateStrategy(book: StudioBook, id: string): StoredStrategy | null {
  const src = book.strategies.find((s) => s.id === id);
  if (!src) return null;
  const copy: StoredStrategy = { ...JSON.parse(JSON.stringify(src)), id: newStrategyId(), name: `${src.name} copy` };
  book.strategies.push(copy);
  book.activeId = copy.id;
  return copy;
}
/** Remove a strategy; if it was selected, selection falls back to none. */
export function removeStrategy(book: StudioBook, id: string): void {
  book.strategies = book.strategies.filter((s) => s.id !== id);
  if (book.activeId === id) book.activeId = null;
}
/** Select a strategy (or pass null to deselect — the default state). */
export function setActiveStrategy(book: StudioBook, id: string | null): void {
  book.activeId = id && book.strategies.some((s) => s.id === id) ? id : null;
}
